import type { ZodSchema } from "zod";

import { trackMeasurement } from "@/analytics/track";
import type { NatsCaller } from "@/backend/natsstubs/NatsCaller";
import { ApiError, TimeoutError } from "@/backend/natsstubs/error";
import { CancelError } from "@/error/CancelError";
import { formatState, useConnectionStore } from "@/store/connection";
import { useStatisticsStore } from "@/store/statistics";
import type { UnknownRecord } from "@/utils/type";

import { BackendError } from "../BackendError";

export class NatsCallerWrapper {
  constructor(private natsCaller: NatsCaller) {}

  async request<T>(
    subject: string,
    action: string,
    payload: UnknownRecord,
    schema: ZodSchema<T>,
    timeout?: number,
    retry = 0,
  ): Promise<T> {
    const reconnects = useConnectionStore().reconnects;
    const startTime = performance.now();
    try {
      const res = await this.natsCaller.request<T>(subject, action, payload, schema, timeout);
      const elapsedTime = performance.now() - startTime;
      trackMeasurement({ type: "nats_rpc_" + action, values: { elapsedTimeMs: elapsedTime } });
      useStatisticsStore().addOk("sends");
      return res;
    } catch (err) {
      useStatisticsStore().addFail("sends", err);
      if (err instanceof TimeoutError) {
        if (!useConnectionStore().isConnected) {
          throw new CancelError("No Network connection");
        }
        if (useConnectionStore().reconnects !== reconnects && retry < 3) {
          return this.request(subject, action, payload, schema, timeout, retry + 1);
        }
        throw new TimeoutError({
          request: err.contexts.request,
          response: {
            ...err.contexts.response,
            connections: useConnectionStore().wsStates.get().map(formatState),
          },
        });
      }
      if (err instanceof ApiError) {
        throw new BackendError(err.name, err.message, {
          request: err.contexts.request,
          response: {
            ...err.contexts.response,
            connections: useConnectionStore().wsStates.get().map(formatState),
          },
        });
      }
      throw err;
    }
  }
}
